/**
 * Single source of truth for Doimpo Custom Software Solutions (/solutions & /solutions/[slug]).
 * Engineered for problem-first B2B conversion, SEO, AEO (Answer Engine Optimization),
 * and GEO (Generative Engine Optimization across US, UK, AU, UAE, and India).
 */

export type SolutionTier = "capability" | "vertical" | "brand";

export type SolutionHeadache = {
  symptom: string;
  title: string;
  body: string;
  fix: string;
  image: string;
  imageAlt: string;
};

export type SolutionShiftRow = {
  before: string;
  after: string;
};

export type SolutionModule = {
  tag: string;
  title: string;
  summary: string;
};

export type SolutionProof = {
  client: string;
  category: string;
  hook: string;
  outcome: string;
  image: string;
  href: string;
};

export type SolutionFaq = {
  q: string;
  a: string;
};

export type SolutionCategory = {
  slug: string;
  tier: SolutionTier;
  shortLabel: string;
  label: string;
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  subhead: string;
  /** Direct 45-word answer block for AI Overviews, Perplexity, ChatGPT & featured snippets. */
  aeoSummary: string;
  heroImage: string;
  heroImageAlt: string;
  ogImage: string;
  /** Key numbers / guarantees displayed in the hero trust strip. */
  metrics: { value: string; label: string }[];
  /** Primary CTA hook label */
  primaryCtaLabel: string;
  /** Interactive funnel step 1 prompt & options */
  diagnosticPrompt: string;
  diagnosticOptions: { id: string; label: string; hint: string }[];
  /** The 3 signature headaches we fix */
  headaches: [SolutionHeadache, SolutionHeadache, SolutionHeadache];
  /** Before vs After architecture shift */
  shiftTitle: string;
  shiftSubtitle: string;
  shiftRows: SolutionShiftRow[];
  /** Core modules included in the solution */
  modules: SolutionModule[];
  /** Real portfolio proof */
  proof: SolutionProof;
  /** AEO / GEO FAQ schema entries */
  faq: SolutionFaq[];
  /** Related service slug for cross-linking */
  relatedServiceSlug: string;
  /** SEO Metadata */
  seoTitle: string;
  seoDescription: string;
};

export const SOLUTIONS_HUB_META = {
  path: "/solutions",
  url: "https://doimpo.com/solutions",
  title: "Custom Software Solutions | Own the IP, Skip the Seat Tax | doimpo",
  description:
    "Custom CRMs, ERPs, AI agents, clinic and logistics platforms, and B2B dealer portals you own 100%. Fixed scope. US, UK, AU, UAE & India.",
  ogImage: "https://doimpo.com/media/brand/og-doimpo-solutions.jpg",
  ogImageAlt: "doimpo custom software solutions — systems you own, not per-seat SaaS.",
  heroImage: "/media/images/solutions/solutions-hub.webp",
};

export const SOLUTIONS: SolutionCategory[] = [
  {
    slug: "custom-crm-portals",
    tier: "capability",
    shortLabel: "CRM & PORTALS",
    label: "Custom CRM, Sales & Client Portals",
    eyebrow: "Custom CRM & Client Portals",
    headline: "Stop paying the",
    headlineAccent: "per-seat tax.",
    subhead:
      "A branded CRM and client portal shaped to your pipeline—owned by you, not rented per seat.",
    aeoSummary:
      "Custom CRM and client portals by doimpo replace per-seat SaaS with a proprietary sales and delivery workspace you own 100%.",
    heroImage: "/media/images/solutions/custom-crm-portals.webp",
    heroImageAlt: "Sales and operations leaders reviewing a custom client portal together",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-custom-crm-portals.jpg",
    metrics: [
      { value: "$0", label: "Per-seat license fees" },
      { value: "6–8 wks", label: "To live V1 launch" },
      { value: "100%", label: "Code & IP ownership" },
    ],
    primaryCtaLabel: "Map your CRM scope",
    diagnosticPrompt: "Which CRM or client bottleneck is costing you the most right now?",
    diagnosticOptions: [
      {
        id: "seat-tax",
        label: "Runaway SaaS seat costs",
        hint: "Paying thousands a month for features your reps never touch",
      },
      {
        id: "rigid-pipeline",
        label: "Rigid quoting & pipeline stages",
        hint: "Off-the-shelf CRM can't model your real deal or approval logic",
      },
      {
        id: "scattered-clients",
        label: "Clients chasing updates in WhatsApp & email",
        hint: "No single branded portal for files, milestones, and invoices",
      },
      {
        id: "disconnected-billing",
        label: "Sales and finance never match",
        hint: "Closed deals require manual re-entry into billing and delivery",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE SEAT TAX",
        title: "Paying enterprise rent for 15% of the features",
        body: "Every new hire triggers another monthly license tier—yet your team still exports to spreadsheets to get real work done.",
        fix: "Unlimited staff and client seats with zero recurring license penalties.",
        image: "/media/images/solutions/custom-crm-portals.webp",
        imageAlt: "Team reviewing sales pipeline metrics on a clean custom screen",
      },
      {
        symptom: "HEADACHE 02 · RIGID WORKFLOWS",
        title: "Bending your business to fit someone else's software",
        body: "Multi-stage quotes, custom margin rules, and regional compliance get duct-taped together with brittle workarounds.",
        fix: "Data models, stage gates, and pricing calculators built to your exact rules.",
        image: "/media/images/deck/deck-problem.webp",
        imageAlt: "Founder frustrated by fragmented software tools",
      },
      {
        symptom: "HEADACHE 03 · MESSY HANDOFFS",
        title: "Clients lost between email, WhatsApp, and Drive links",
        body: "The moment a deal closes, onboarding turns into 40 scattered messages and lost attachments.",
        fix: "One white-glove client portal for onboarding, approvals, live status, and billing.",
        image: "/media/images/deck/deck-team.webp",
        imageAlt: "Unified team collaborating on a single client delivery platform",
      },
    ],
    shiftTitle: "From rented bloat to an engine you own.",
    shiftSubtitle: "What changes the week your team moves into a portal built for them.",
    shiftRows: [
      { before: "$3,000–$8,000/mo in compounding seat licenses", after: "One asset on your balance sheet—unlimited users" },
      { before: "Reps fighting 40 irrelevant form fields", after: "3-click deal updates built for speed on mobile & desktop" },
      { before: "“Can you resend that link?” in WhatsApp threads", after: "Branded client portal with live milestones & 1-click sign-off" },
      { before: "Manual handoff from sales to ops and finance", after: "Closed-won automatically triggers projects, tasks, and invoices" },
    ],
    modules: [
      {
        tag: "PIPELINE ENGINE",
        title: "Custom Deal & Quoting Logic",
        summary: "Multi-currency quoting, margin guardrails, and automated approval routing.",
      },
      {
        tag: "CLIENT PORTAL",
        title: "White-Label Customer Workspace",
        summary: "Branded login where clients upload docs, approve deliverables, and pay invoices.",
      },
      {
        tag: "RBAC & SECURITY",
        title: "Role-Based Access & Audit Trails",
        summary: "Granular permissions for reps, managers, partners, and external clients.",
      },
      {
        tag: "LIVE TELEMETRY",
        title: "Executive Revenue & Velocity Board",
        summary: "Real-time conversion rates, rep velocity, and cash-flow forecasting without CSV exports.",
      },
    ],
    proof: {
      client: "Execura Suite",
      category: "B2B Workspace & Pipeline",
      hook: "Replaced 6 disconnected tools with one guided 28-step workspace.",
      outcome: "Unified lead capture, client delivery, and automated reporting under one login.",
      image: "/media/portfolio/screenshots/execura-home.webp",
      href: "/portfolio/execura",
    },
    faq: [
      {
        q: "How long does it take to build a custom CRM or client portal?",
        a: "Most production V1 CRMs and portals launch in 6–8 weeks.",
      },
      {
        q: "Can you migrate our existing data from HubSpot, Salesforce, Zoho, or spreadsheets?",
        a: "Yes—we migrate contacts, deals, notes, and attachments with cleanup and zero data loss.",
      },
      {
        q: "Who owns the source code and data?",
        a: "You own 100% of the code, data, and IP—no seat fees, no lock-in.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "Custom CRM & Client Portal Development | Zero Per-Seat Tax | doimpo",
    seoDescription:
      "Replace bloated SaaS CRMs and scattered client threads with a bespoke CRM & branded client portal built around your exact sales and delivery workflow. 100% IP ownership.",
  },
  {
    slug: "custom-erp-operations",
    tier: "capability",
    shortLabel: "ERP & OPS",
    label: "Operations, ERP & Inventory Systems",
    eyebrow: "Operations, ERP & Inventory",
    headline: "Retire the fragile",
    headlineAccent: "master spreadsheet.",
    subhead:
      "One live ops backbone for procurement, stock, dispatch, and margin—no spreadsheet roulette.",
    aeoSummary:
      "doimpo builds modular ERP and inventory platforms that replace fragile spreadsheets with live procurement, stock, and margin control.",
    heroImage: "/media/images/solutions/custom-erp-operations.webp",
    heroImageAlt: "Operations manager inspecting live warehouse inventory and fulfillment data",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-custom-erp-operations.jpg",
    metrics: [
      { value: "74%", label: "Less manual data entry" },
      { value: "Live", label: "Multi-site stock & margin" },
      { value: "Zero", label: "Broken spreadsheet formulas" },
    ],
    primaryCtaLabel: "Scope your ops build",
    diagnosticPrompt: "Where does your daily operation lose the most time or margin?",
    diagnosticOptions: [
      {
        id: "spreadsheets",
        label: "Critical ops trapped in Excel / Sheets",
        hint: "Version conflicts, broken formulas, and Friday afternoon reporting marathons",
      },
      {
        id: "stock-blindspots",
        label: "Inventory & warehouse blind spots",
        hint: "Stockouts, over-ordering, or discrepancies across locations",
      },
      {
        id: "po-approvals",
        label: "Manual PO, vendor & billing approvals",
        hint: "Purchase orders and GRNs chased over WhatsApp and paper slips",
      },
      {
        id: "legacy-erp",
        label: "Bloated ERP nobody wants to use",
        hint: "Clunky screens that force floor staff back to pen and paper",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · SPREADSHEET ROULETTE",
        title: "A $10M operation balanced on a fragile Google Sheet",
        body: "One accidental paste or deleted row corrupts purchasing numbers, and nobody notices until stock runs dry.",
        fix: "Validated database workflows with role locks, barcode/QR scan input, and full audit history.",
        image: "/media/images/solutions/custom-erp-operations.webp",
        imageAlt: "Modern warehouse and operations control system",
      },
      {
        symptom: "HEADACHE 02 · FRIDAY BLINDNESS",
        title: "Waiting until Friday to know Tuesday's margin",
        body: "Leadership can’t see real-time unit economics because finance, warehouse, and sales reconcile numbers manually once a week.",
        fix: "Live P&L, SKU-level margin, and fulfillment dashboards updated on every scan.",
        image: "/media/images/sections/optimize.webp",
        imageAlt: "Operations leadership reviewing live financial and stock metrics",
      },
      {
        symptom: "HEADACHE 03 · APPROVAL BOTTLENECKS",
        title: "Purchase orders and vendor bills stuck in chat threads",
        body: "Approvals happen in WhatsApp voice notes, leaving finance to guess what was authorized and what was actually delivered.",
        fix: "1-tap mobile PO approvals, automated 3-way matching (PO, GRN, Invoice), and vendor self-service.",
        image: "/media/images/sections/propel.webp",
        imageAlt: "Team aligning on procurement and supply chain workflows",
      },
    ],
    shiftTitle: "One operational heartbeat across floor, field, and finance.",
    shiftSubtitle: "You don’t need a 2-year SAP project—just the modules that run your floor.",
    shiftRows: [
      { before: "14 linked spreadsheets with conflicting numbers", after: "Single source of truth across every warehouse & branch" },
      { before: "Manual POs, WhatsApp approvals & paper GRNs", after: "Automated 3-way match from requisition to vendor payout" },
      { before: "Clunky 1990s ERP screens staff refuse to use", after: "Fast tablet & mobile interfaces learned in 15 minutes" },
      { before: "End-of-month margin surprises", after: "Live unit economics and low-stock alerts on every order" },
    ],
    modules: [
      {
        tag: "INVENTORY & WMS",
        title: "Multi-Location Stock & Batch Tracking",
        summary: "Real-time stock levels, QR/barcode scanning, reorder triggers, and transfer ledgers.",
      },
      {
        tag: "PROCUREMENT",
        title: "Purchase Orders & Vendor Portal",
        summary: "Automated RFQ comparisons, tiered PO approvals, and supplier delivery scheduling.",
      },
      {
        tag: "PRODUCTION & DISPATCH",
        title: "Work Orders, BOM & Fulfillment",
        summary: "Track raw materials to finished goods, QA checkpoints, and dispatch manifests.",
      },
      {
        tag: "FINANCE SYNC",
        title: "Accounting & Tax Integration",
        summary: "Bi-directional sync with Xero, QuickBooks, Tally, Zoho Books, or NetSuite.",
      },
    ],
    proof: {
      client: "Trevia EV",
      category: "Mobility & Operations",
      hook: "Connected product catalog, dealer inquiries, and showroom operations.",
      outcome: "Streamlined multi-model specifications and buyer routing into one cohesive platform.",
      image: "/media/portfolio/screenshots/trevia-home.webp",
      href: "/portfolio/trevia",
    },
    faq: [
      {
        q: "Why build a modular custom ERP instead of buying NetSuite, SAP, or Odoo?",
        a: "Off-the-shelf ERPs still need months of customization—and charge per seat. We ship only your modules in 8–12 weeks.",
      },
      {
        q: "Will it integrate with our existing accounting software?",
        a: "Yes. We sync invoices, POs, and payments with QuickBooks, Xero, Zoho Books, Tally, or SAP.",
      },
      {
        q: "Can warehouse and floor staff use it on mobile phones or rugged tablets?",
        a: "Yes—responsive/PWA with camera barcode scanning and touch-first floor controls.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "Custom ERP, Inventory & Operations Software Development | doimpo",
    seoDescription:
      "Replace fragile spreadsheets and clunky legacy ERPs with a modular, cloud-native operations platform for procurement, inventory, dispatch, and live margin tracking.",
  },
  {
    slug: "ai-workflow-automation",
    tier: "capability",
    shortLabel: "AI & AUTOMATION",
    label: "AI Agents & Autonomous Workflow Automation",
    eyebrow: "AI Agents & Workflow Automation",
    headline: "Fire the copy-paste",
    headlineAccent: "busywork.",
    subhead:
      "Private AI agents and pipelines that cut copy-paste busywork out of your real tools.",
    aeoSummary:
      "doimpo ships private AI agents, document pipelines, and 24/7 workflows wired into your stack—without leaking proprietary data.",
    heroImage: "/media/images/solutions/ai-workflow-automation.webp",
    heroImageAlt: "Engineers and operators designing an autonomous AI workflow pipeline",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-ai-workflow-automation.jpg",
    metrics: [
      { value: "< 15s", label: "24/7 lead & ticket response" },
      { value: "10–25 hrs", label: "Saved per employee weekly" },
      { value: "Private", label: "Zero public model training" },
    ],
    primaryCtaLabel: "Scope your AI workflow",
    diagnosticPrompt: "Which repetitive workflow is draining your team's hours every week?",
    diagnosticOptions: [
      {
        id: "doc-ocr",
        label: "Manual PDF, invoice & form entry",
        hint: "Staff reading attachments and typing rows into CRM or ERP",
      },
      {
        id: "slow-leads",
        label: "Leads & WhatsApp enquiries waiting hours",
        hint: "High-intent prospects going cold overnight or on weekends",
      },
      {
        id: "trapped-knowledge",
        label: "Team hunting through SOPs & past proposals",
        hint: "institutional knowledge buried in Drive folders, PDFs, and Slack",
      },
      {
        id: "reporting-drudgery",
        label: "Weekly reporting & follow-up drudgery",
        hint: "Hours spent chasing status updates and compiling summaries",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · HUMAN MIDDLEWARE",
        title: "Paying skilled specialists to copy-paste between tabs",
        body: "Every invoice, intake form, or vendor email gets read by a human and re-typed into three systems—slow, expensive, and error-prone.",
        fix: "AI document extraction that reads PDFs/emails, validates rules, and writes straight to your database.",
        image: "/media/images/solutions/ai-workflow-automation.webp",
        imageAlt: "Automated AI document and workflow processing",
      },
      {
        symptom: "HEADACHE 02 · THE 11PM LEAK",
        title: "High-intent leads going cold while your team sleeps",
        body: "When a prospect asks a specific question at 10pm, waiting until 10am the next morning means they already booked a competitor.",
        fix: "24/7 AI concierges that answer from your data and book the calendar.",
        image: "/media/images/blog/multimodal-ai.webp",
        imageAlt: "24/7 autonomous AI concierge handling customer queries",
      },
      {
        symptom: "HEADACHE 03 · TRAPPED BRAINPOWER",
        title: "Ten years of company answers buried in 4,000 folders",
        body: "New hires interrupt senior leaders 20 times a day because nobody can find the right SOP, contract clause, or past quote.",
        fix: "A private RAG knowledge engine that answers staff questions with exact citations in two seconds.",
        image: "/media/images/blog/enterprise-search.webp",
        imageAlt: "Enterprise knowledge search and AI assistant",
      },
    ],
    shiftTitle: "Practical AI that ships to production—not a parlor trick.",
    shiftSubtitle: "Deterministic code plus LLMs—auditable, secure, and production-ready.",
    shiftRows: [
      { before: "15 minutes per invoice or intake packet", after: "Parsed, validated & synced in 4 seconds with human-in-the-loop review" },
      { before: "After-hours enquiries answered the next day", after: "Qualified, answered from your docs & booked on your calendar in <15s" },
      { before: "Generic chatbots that hallucinate policies", after: "Grounded RAG agents locked strictly to your verified knowledge base" },
      { before: "Proprietary data pasted into public AI tools", after: "Private cloud / VPC endpoints with zero external model training" },
    ],
    modules: [
      {
        tag: "AI CONCIERGE",
        title: "24/7 WhatsApp, Voice & Web Agents",
        summary: "Qualify leads, answer technical questions, and book calendar slots directly.",
      },
      {
        tag: "DOCUMENT AI",
        title: "Intelligent OCR & Data Extraction",
        summary: "Turn unstructured PDFs, invoices, lab reports, and contracts into structured database rows.",
      },
      {
        tag: "PRIVATE RAG",
        title: "Internal Knowledge & Proposal Copilot",
        summary: "Instant cited answers across your SOPs, contracts, Slack history, and past pitches.",
      },
      {
        tag: "AGENTIC OPS",
        title: "Multi-Step Autonomous Workflows",
        summary: "Trigger-to-outcome pipelines with human approval gates for high-stakes actions.",
      },
    ],
    proof: {
      client: "DebtClear AI",
      category: "Fintech AI Automation",
      hook: "Turned complex financial statement uploads into an instant payoff roadmap.",
      outcome: "Automated document parsing and personalized plan generation in a single upload flow.",
      image: "/media/portfolio/screenshots/debtor-home.webp",
      href: "/portfolio/debtor",
    },
    faq: [
      {
        q: "Is our company and customer data safe when using your AI solutions?",
        a: "Yes—private/VPC endpoints, no public model training on your data, full audit trails.",
      },
      {
        q: "How do you prevent the AI from hallucinating or making costly mistakes?",
        a: "We use Retrieval-Augmented Generation (RAG) with strict citation guardrails, schema-validated tool calls, and human-in-the-loop approval thresholds for financial or sensitive actions.",
      },
      {
        q: "Can we start with just one high-ROI workflow?",
        a: "Absolutely. Most clients start with one document or lead workflow and expand from there.",
      },
    ],
    relatedServiceSlug: "ai-solutions",
    seoTitle: "Custom AI Agents & Workflow Automation Development | doimpo",
    seoDescription:
      "Eliminate manual copy-paste busywork with private enterprise AI agents, RAG knowledge assistants, document OCR pipelines, and 24/7 WhatsApp/voice lead automation.",
  },
  {
    slug: "api-system-integration",
    tier: "capability",
    shortLabel: "API & INTEGRATION",
    label: "Systems Integration, API & Data Unification",
    eyebrow: "API & Systems Integration",
    headline: "Nine tools.",
    headlineAccent: "Finally speaking one truth.",
    subhead:
      "Middleware and dashboards that make every tool share one live truth.",
    aeoSummary:
      "doimpo builds production API middleware and executive dashboards so every system shares one live operational truth.",
    heroImage: "/media/images/solutions/api-system-integration.webp",
    heroImageAlt: "Unified executive analytics and API integration architecture on screen",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-api-system-integration.jpg",
    metrics: [
      { value: "99.9%", label: "Sync reliability with retry queues" },
      { value: "1 Screen", label: "Unified executive visibility" },
      { value: "Real-Time", label: "Bi-directional webhook sync" },
    ],
    primaryCtaLabel: "Unify your stack",
    diagnosticPrompt: "What's breaking between your current software tools?",
    diagnosticOptions: [
      {
        id: "zapier-spaghetti",
        label: "Fragile Zapier / Make spaghetti",
        hint: "Silent sync failures, duplicate records, and runaway task bills",
      },
      {
        id: "conflicting-numbers",
        label: "Sales, ops & finance show different numbers",
        hint: "No unified executive dashboard across your whole funnel",
      },
      {
        id: "legacy-silo",
        label: "A core tool with no pre-built connector",
        hint: "Industry software or legacy database that can't talk to modern apps",
      },
      {
        id: "partner-api",
        label: "Need a secure external API for partners/clients",
        hint: "Customers or vendors need programmatic access to orders or status",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · ZAPIER SPAGHETTI",
        title: "80 brittle no-code zaps that break every Tuesday",
        body: "One API rate limit or field rename silently drops 200 customer orders, and nobody notices until angry emails arrive.",
        fix: "Dead-letter queues, automatic retries, schema validation, and instant Slack/email failure alerts.",
        image: "/media/images/solutions/api-system-integration.webp",
        imageAlt: "Clean data telemetry and API health monitoring dashboard",
      },
      {
        symptom: "HEADACHE 02 · DUPLICATE DATA SILOS",
        title: "Three versions of the same customer across four tools",
        body: "Support doesn’t know an invoice is overdue, and sales doesn’t know a shipment was delayed.",
        fix: "A unified master record layer that keeps CRM, billing, and fulfillment in lockstep.",
        image: "/media/images/blog/conversational-search.webp",
        imageAlt: "Connected network nodes representing unified enterprise data",
      },
      {
        symptom: "HEADACHE 03 · BOARDROOM GUESSWORK",
        title: "Stitching 6 CSV exports together before every Monday meeting",
        body: "You can't see CAC, LTV, pipeline velocity, and cash collected on the same screen without three hours of VLOOKUPs.",
        fix: "One live executive command center pulling directly from ads, CRM, Stripe/bank, and ERP.",
        image: "/media/images/blueprint/analyze.webp",
        imageAlt: "Executive reviewing unified real-time business metrics",
      },
    ],
    shiftTitle: "Replace duct tape with engineered plumbing.",
    shiftSubtitle: "Keep the tools your team loves—just make them behave like one system.",
    shiftRows: [
      { before: "Silent no-code failures & missing customer records", after: "Idempotent queues with guaranteed delivery & audit logs" },
      { before: "Manual CSV imports between CRM and accounting", after: "Sub-second bi-directional webhook synchronization" },
      { before: "Monday morning spent building board slides", after: "Live command dashboard with drill-down from ad click to bank deposit" },
      { before: "Vendor lock-in to expensive middleware tiers", after: "Lightweight serverless middleware you own and control" },
    ],
    modules: [
      {
        tag: "MIDDLEWARE",
        title: "Fault-Tolerant Sync Engine",
        summary: "Event-driven queues, rate-limit handling, deduplication, and automatic replay.",
      },
      {
        tag: "COMMAND CENTER",
        title: "Unified Executive KPI Dashboard",
        summary: "Cross-platform attribution joining marketing spend, CRM pipeline, and net revenue.",
      },
      {
        tag: "CUSTOM APIs",
        title: "REST & GraphQL Gateway Engineering",
        summary: "Secure, documented APIs with OAuth/API keys for your mobile apps or B2B partners.",
      },
      {
        tag: "DATA WAREHOUSE",
        title: "Clean ETL & Reporting Layer",
        summary: "Normalized Postgres/BigQuery data layer ready for BI and AI querying.",
      },
    ],
    proof: {
      client: "Doimpo Studio & Collab",
      category: "Connected Growth Ecosystem",
      hook: "Unified content, CRM leads, analytics, and multi-channel publishing.",
      outcome: "Eliminated manual asset handoffs with one API-driven publishing and telemetry backbone.",
      image: "/media/portfolio/screenshots/doimpo-studio-home.webp",
      href: "/portfolio/doimpo-studio",
    },
    faq: [
      {
        q: "When should we replace Zapier or Make with custom API middleware?",
        a: "When volume, sensitive data, or multi-step logic makes Zapier fragile, custom middleware pays for itself.",
      },
      {
        q: "Can you integrate with older on-premise or industry-specific software?",
        a: "Yes. We wrap legacy and industry systems behind modern APIs without a rip-and-replace.",
      },
      {
        q: "How do we monitor if an external API goes down?",
        a: "Live health dashboards, structured logs, retries, and instant outage alerts are included.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "Custom API Integration & Data Unification Services | doimpo",
    seoDescription:
      "Connect your CRM, ERP, accounting, e-commerce, and marketing tools into a single source of truth with fault-tolerant custom API middleware and executive dashboards.",
  },
  {
    slug: "saas-product-development",
    tier: "capability",
    shortLabel: "SAAS & MVP",
    label: "SaaS MVP & Product Engineering",
    eyebrow: "SaaS & Product Engineering",
    headline: "From domain insight",
    headlineAccent: "to paying tenants.",
    subhead:
      "Multi-tenant SaaS from insight to paying tenants—brand, product, billing, launch.",
    aeoSummary:
      "doimpo engineers multi-tenant SaaS products end-to-end—brand, product, billing, and launch—in weeks, not quarters.",
    heroImage: "/media/images/solutions/saas-product-development.webp",
    heroImageAlt: "Founders and product engineers launching a SaaS application",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-saas-product-development.jpg",
    metrics: [
      { value: "6–10 wks", label: "From blueprint to live V1" },
      { value: "Multi-Tenant", label: "Built to scale from day one" },
      { value: "1 Roof", label: "Brand + Product + Go-to-Market" },
    ],
    primaryCtaLabel: "Get your MVP blueprint",
    diagnosticPrompt: "Where are you in your SaaS product journey right now?",
    diagnosticOptions: [
      {
        id: "new-mvp",
        label: "Turning a validated idea or service into SaaS",
        hint: "Need a sharp V1 fast to win first paying customers",
      },
      {
        id: "rescue-rebuild",
        label: "Stalled build or fragile prototype",
        hint: "Previous build is hitting a wall under real usage",
      },
      {
        id: "enterprise-ready",
        label: "Adding multi-tenancy, SSO & enterprise billing",
        hint: "Ready for B2B orgs, RBAC, and billing scale",
      },
      {
        id: "product-plus-gtm",
        label: "Great product, weak positioning & onboarding",
        hint: "Need brand identity, high-converting product site, and activation loops",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE 9-MONTH MIRAGE",
        title: "Burning runway on endless specs before a single user logs in",
        body: "Bloated dev shops pad timelines with features you don’t need in V1, missing your market window.",
        fix: "Ruthless 6–10 week V1 scoping focused on the core habit loop that makes users pay.",
        image: "/media/images/solutions/saas-product-development.webp",
        imageAlt: "Fast, focused SaaS product engineering sprint",
      },
      {
        symptom: "HEADACHE 02 · THROWAWAY PROTOTYPES",
        title: "Having to rewrite the entire codebase after your first 50 users",
        body: "Quick-and-dirty MVPs skip tenant isolation, permissions, and billing webhooks—turning early traction into a technical crisis.",
        fix: "Clean multi-tenant architecture, Stripe billing, and RBAC baked in from sprint one.",
        image: "/media/images/deck/deck-saas.webp",
        imageAlt: "Founders celebrating a clean SaaS launch",
      },
      {
        symptom: "HEADACHE 03 · THE SILENT LAUNCH",
        title: "Shipping great code with a forgettable brand and zero sign-ups",
        body: "A dev shop hands you a repo—then you hire three more vendors for brand, billing, and launch.",
        fix: "One team builds the product UI, brand identity, marketing site, and onboarding emails together.",
        image: "/media/images/services/branding.webp",
        imageAlt: "Cohesive SaaS brand and product interface design",
      },
    ],
    shiftTitle: "Don’t hire a dev shop when you need a product partner.",
    shiftSubtitle: "Brand, engineering, and launch in one room.",
    shiftRows: [
      { before: "Separate agency for brand, UI, code, and launch", after: "One integrated squad from positioning to production" },
      { before: "6–9 months of opaque hourly billing", after: "Fixed-scope 6–10 week milestones with weekly live demos" },
      { before: "Bolting on billing and org permissions later", after: "Multi-tenant auth, RBAC, and Stripe subscriptions ready on day one" },
      { before: "Visitors need a 30-min demo just to understand it", after: "A product site and onboarding flow that deliver a first win in 3 minutes" },
    ],
    modules: [
      {
        tag: "FOUNDATION",
        title: "Multi-Tenant Auth, Orgs & RBAC",
        summary: "Workspace switching, team invites, SSO readiness, and strict row-level tenant isolation.",
      },
      {
        tag: "MONETIZATION",
        title: "Subscriptions, Usage & Tiered Billing",
        summary: "Stripe/Razorpay checkout, metered usage tracking, trials, and self-serve plan upgrades.",
      },
      {
        tag: "PRODUCT UX",
        title: "Design System & High-Craft Interface",
        summary: "Distinctive brand identity, component library, and responsive web + mobile experiences.",
      },
      {
        tag: "ACTIVATION",
        title: "Go-To-Market Site & Onboarding Loops",
        summary: "Conversion-engineered landing pages, interactive product tours, and lifecycle nudges.",
      },
    ],
    proof: {
      client: "mairaki & ShepherdOS",
      category: "Multi-Tenant SaaS & Apps",
      hook: "Designed, branded, and engineered complete web and mobile SaaS ecosystems.",
      outcome: "Cohesive brand identity, subscription architecture, and product experience shipped under one roof.",
      image: "/media/portfolio/screenshots/shepherdos-home.webp",
      href: "/portfolio/shepherdos",
    },
    faq: [
      {
        q: "How much does it cost and how long does it take to build a SaaS MVP?",
        a: "Production SaaS V1 typically ships in 6–10 weeks with fixed milestone pricing.",
      },
      {
        q: "Do you also handle the brand identity and marketing website for the SaaS product?",
        a: "Yes—brand, product UI, and marketing site ship as one coherent launch package.",
      },
      {
        q: "What happens after V1 launches?",
        a: "You own 100% of the repo and infrastructure from day one.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "SaaS MVP & Custom Product Engineering Partner | doimpo",
    seoDescription:
      "Turn your industry insight into a scalable multi-tenant SaaS product in 6–10 weeks. Brand identity, UI/UX, full-stack engineering, billing, and launch under one roof.",
  },
  {
    slug: "legacy-modernization",
    tier: "capability",
    shortLabel: "MODERNIZATION",
    label: "Legacy Software Modernization & Cloud Migration",
    eyebrow: "Legacy Software Modernization",
    headline: "Rebuild the engine",
    headlineAccent: "mid-flight.",
    subhead:
      "Zero-downtime migration from aging monoliths to fast cloud-native systems.",
    aeoSummary:
      "doimpo modernizes legacy software with zero-downtime migration from aging monoliths to cloud-native systems.",
    heroImage: "/media/images/solutions/legacy-modernization.webp",
    heroImageAlt: "Architects planning a zero-downtime cloud migration and software modernization",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-legacy-modernization.jpg",
    metrics: [
      { value: "Zero", label: "Operational downtime" },
      { value: "3–5×", label: "Faster page & query speed" },
      { value: "100%", label: "Historical data preserved" },
    ],
    primaryCtaLabel: "Request a legacy audit",
    diagnosticPrompt: "What is the biggest risk with your current legacy system?",
    diagnosticOptions: [
      {
        id: "fragile-monolith",
        label: "Every small code change breaks something else",
        hint: "Original developers left years ago and technical debt is stalling new features",
      },
      {
        id: "no-mobile-api",
        label: "Desktop-bound with no mobile or API access",
        hint: "Field teams, customers, and modern AI tools can't connect to the system",
      },
      {
        id: "performance-security",
        label: "Slow load times, crashes & compliance risk",
        hint: "Aging servers and outdated frameworks failing security or customer expectations",
      },
      {
        id: "ux-rebellion",
        label: "Staff & customers hate the dated interface",
        hint: "Losing deals or wasting training hours on a 2012-era UI",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE UNTOUCHABLE BLACK BOX",
        title: "One developer holds the keys—and everyone is afraid to deploy",
        body: "Adding a simple feature takes six weeks because undocumented spaghetti code breaks billing every time someone touches it.",
        fix: "Modular domain services, automated test coverage, and clean CI/CD pipelines.",
        image: "/media/images/solutions/legacy-modernization.webp",
        imageAlt: "Modern cloud architecture replacing legacy monolith",
      },
      {
        symptom: "HEADACHE 02 · TRAPPED ON DESKTOP",
        title: "A business that moves on phones tied to a 2010 desktop screen",
        body: "Executives, field reps, and customers have to wait until they’re back at an office PC to approve or check anything.",
        fix: "A modern API layer and responsive web/mobile UI layered cleanly over your core logic.",
        image: "/media/images/services/app-dev.webp",
        imageAlt: "Mobile-first interface connected to enterprise backend",
      },
      {
        symptom: "HEADACHE 03 · FEAR OF THE BIG BANG",
        title: "Putting off modernization because a 'big switch-over' feels too risky",
        body: "You can't afford to shut down operations for a week or risk losing a decade of customer records.",
        fix: "Phased strangler-fig migration—replace one workflow at a time while both systems stay in sync.",
        image: "/media/images/blueprint/roadmap.webp",
        imageAlt: "Phased zero-downtime migration roadmap",
      },
    ],
    shiftTitle: "No big-bang rewrites. Just steady, zero-downtime replacement.",
    shiftSubtitle: "Modern API wrap first, high-impact screens next, legacy retired safely.",
    shiftRows: [
      { before: "Risky 18-month 'big bang' rewrite that stalls", after: "Phased strangler-fig rollout—first modern module live in 4–6 weeks" },
      { before: "Desktop-only screens requiring VPNs", after: "Cloud-native web & mobile access with modern SSO and 2FA" },
      { before: "Closed database that blocks AI and partner integrations", after: "Clean REST/GraphQL API layer ready for automation" },
      { before: "10-second report queries that lock the database", after: "Indexed cloud Postgres & read replicas returning in milliseconds" },
    ],
    modules: [
      {
        tag: "PHASE 1 · AUDIT",
        title: "Codebase, Schema & Risk Diagnostic",
        summary: "Map dependencies, security vulnerabilities, and isolate the highest-ROI module to modernize first.",
      },
      {
        tag: "PHASE 2 · BRIDGE",
        title: "API Facade & Real-Time Data Sync",
        summary: "Wrap legacy databases in a secure modern API so new and old screens run side-by-side.",
      },
      {
        tag: "PHASE 3 · UX SHIFT",
        title: "Modern Web & Mobile Interface Rebuild",
        summary: "Fast, accessible React/TypeScript interfaces that cut staff clicks in half.",
      },
      {
        tag: "PHASE 4 · CUTOVER",
        title: "Cloud Infrastructure & Monolith Retirement",
        summary: "Automated backups, containerization, observability, and safe decommissioning of old servers.",
      },
    ],
    proof: {
      client: "South Asian Liver Institute",
      category: "Platform Modernization",
      hook: "Transformed a dated web presence into a fast, structured clinical authority platform.",
      outcome: "Modern architecture, clear patient pathways, and search-ready performance.",
      image: "/media/portfolio/screenshots/sali-home.webp",
      href: "/portfolio/sali",
    },
    faq: [
      {
        q: "Will our daily operations have to pause during the modernization?",
        a: "No. We migrate mid-flight with parallel cutovers and zero planned downtime.",
      },
      {
        q: "Can we modernize just the customer-facing portal or mobile app first?",
        a: "Yes—and wrapping a modern UI on the old core is often the highest-ROI first move.",
      },
      {
        q: "What legacy stacks do you work with?",
        a: "Yes—PHP, .NET, Java, FoxPro/Access, old SQL, WordPress/Drupal, and no-code monoliths into modern TypeScript stacks.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "Legacy Software Modernization & Cloud Migration | Zero Downtime | doimpo",
    seoDescription:
      "Modernize aging desktop, PHP, or .NET monoliths into fast, cloud-native web and mobile systems without disrupting daily operations. Book an architecture assessment.",
  },
  {
    slug: "healthcare-clinics",
    tier: "vertical",
    shortLabel: "HEALTHCARE & CLINICS",
    label: "Clinics, Healthcare & Patient Journey Software",
    eyebrow: "Healthcare & Clinic Software",
    headline: "Every consult tracked.",
    headlineAccent: "Every patient remembered.",
    subhead:
      "Clinic software that connects front desk, care, and recall—without EHR bloat.",
    aeoSummary:
      "doimpo builds clinic platforms that unify scheduling, care workflows, and patient recall without enterprise EHR bloat.",
    heroImage: "/media/images/solutions/healthcare-clinics.webp",
    heroImageAlt: "Doctor and clinic coordinator reviewing a patient treatment plan on a tablet",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-healthcare-clinics.jpg",
    metrics: [
      { value: "24/7", label: "Instant patient enquiry response" },
      { value: "1 Tap", label: "Consent, photo vault & recall" },
      { value: "Zero", label: "Lost post-consult follow-ups" },
    ],
    primaryCtaLabel: "Audit clinic workflow",
    diagnosticPrompt: "Where do patients or staff feel the most friction in your clinic?",
    diagnosticOptions: [
      {
        id: "missed-enquiries",
        label: "After-hours enquiries & DMs slipping away",
        hint: "After-hours WhatsApp leads booking somewhere else",
      },
      {
        id: "consult-drop",
        label: "First consults that quietly don't rebook",
        hint: "Great clinical consultation, but no automated treatment plan follow-up",
      },
      {
        id: "photo-consent",
        label: "Before & after photos + consent scattered",
        hint: "Shot on staff phones, paper consent forms, never organized or published",
      },
      {
        id: "disconnected-frontdesk",
        label: "Front desk juggling 6 disconnected tools",
        hint: "Booking diary, EMR notes, WhatsApp, and billing never talk to each other",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · UNPUBLISHED PROOF",
        title: "Before & afters shot on three phones and published never",
        body: "Consent lives on a clipboard, photos live on a nurse’s camera roll, and your best clinical outcomes never build trust online.",
        fix: "A 1-tap clinic capture app that pairs photo, digital consent tier, and treatment tag automatically.",
        image: "/media/images/clinics/clinic-before-after-capture.webp",
        imageAlt: "Clinical before and after photo capture with digital consent",
      },
      {
        symptom: "HEADACHE 02 · NINE TOOLS AT RECEPTION",
        title: "Booking here, notes there, WhatsApp everywhere else",
        body: "Your coordinator spends half the day copying patient details between the booking calendar, WhatsApp, and billing.",
        fix: "One calm reception command view where every enquiry, appointment, and recall sits on a single timeline.",
        image: "/media/images/clinics/clinic-scattered-desk.webp",
        imageAlt: "Clinic reception desk overwhelmed by multiple devices and paper diaries",
      },
      {
        symptom: "HEADACHE 03 · THE QUIET DROP-OFF",
        title: "Great first consult, no structured follow-up, gone in six weeks",
        body: "Patients leave with a printed plan and good intentions—then life gets busy and nobody nudges them at day 3, 14, or 30.",
        fix: "Automated post-consult care sequences, digital treatment plans, and 1-tap WhatsApp rebooking.",
        image: "/media/images/clinics/clinic-consult-review.webp",
        imageAlt: "Doctor reviewing a clear digital consultation and follow-up plan",
      },
    ],
    shiftTitle: "Turn clinical excellence into a calm, repeatable patient engine.",
    shiftSubtitle: "We connect your front door, reception desk, treatment room, and recall loop.",
    shiftRows: [
      { before: "Enquiries lost after 6pm on WhatsApp & Instagram", after: "AI front desk answers from your clinical content & books 24/7" },
      { before: "Paper consent & photos trapped on personal phones", after: "Encrypted photo vault tied to digital consent & patient record" },
      { before: "Consults forgotten when the front desk gets busy", after: "Automated treatment plan reminders & recall loops" },
      { before: "Generic clinic website that looks like a template", after: "Specialist authority platform that earns trust before the call" },
    ],
    modules: [
      {
        tag: "AI FRONT DESK",
        title: "24/7 Patient Triage & Booking Concierge",
        summary: "Answers procedure questions accurately and books qualified slots across web, WhatsApp, and Instagram.",
      },
      {
        tag: "CAPTURE & CONSENT",
        title: "Clinical Photo Vault & Digital Consent",
        summary: "Tag before/after media by procedure with instant patient consent tiers for internal or marketing use.",
      },
      {
        tag: "PATIENT RECALL",
        title: "Post-Consult & Maintenance Automation",
        summary: "Timed care instructions, review requests, and treatment plan rebooking nudges.",
      },
      {
        tag: "AUTHORITY WEB",
        title: "High-Trust Specialist & Hospital Websites",
        summary: "Doctor profiles, condition hubs, and AEO-optimized clinical content that ranks and converts.",
      },
    ],
    proof: {
      client: "Ashok One Hospital & Prof. Dr. Tom Cherian",
      category: "Healthcare & Specialist Platforms",
      hook: "Built high-trust patient portals and clinical pathways for leading hospitals and surgeons.",
      outcome: "Clear service-to-doctor-to-appointment journeys that turn searchers into booked consultations.",
      image: "/media/portfolio/screenshots/ashok-one-hospital-home.webp",
      href: "/portfolio/ashok-one-hospital",
    },
    faq: [
      {
        q: "Do we have to replace our existing clinic booking or EMR software?",
        a: "No. We integrate first and only replace what is blocking growth.",
      },
      {
        q: "How is patient privacy and consent handled?",
        a: "Role-based access, encryption, audit logs, and granular consent tiers—clinical vs marketing.",
      },
      {
        q: "Can we test our clinic's current workflow right now?",
        a: "Yes—use the diagnostic below or the 90-second clinic audit at doimpo.com/clinics.",
      },
    ],
    relatedServiceSlug: "ai-solutions",
    seoTitle: "Custom Clinic & Healthcare Patient Journey Software | doimpo",
    seoDescription:
      "Fix leaked patient enquiries, scattered before-and-after photos, and missed consult rebookings with custom clinic software and AI front-desk automation.",
  },
  {
    slug: "field-service-real-estate",
    tier: "vertical",
    shortLabel: "FIELD OPS & REAL ESTATE",
    label: "Field Service, Real Estate & Construction Ops",
    eyebrow: "Field Service & Property Operations",
    headline: "Bring the job site",
    headlineAccent: "out of WhatsApp.",
    subhead:
      "Field and site ops software built for dusty job sites and clean boardroom numbers.",
    aeoSummary:
      "doimpo builds field-service and real-estate ops platforms for job-site reality and boardroom-ready reporting.",
    heroImage: "/media/images/solutions/field-service-real-estate.webp",
    heroImageAlt: "Architects and field engineers reviewing project plans on site",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-field-service-real-estate.jpg",
    metrics: [
      { value: "Offline", label: "Works in basements & remote sites" },
      { value: "100%", label: "Geo-tagged photo & snag proof" },
      { value: "Live", label: "Site-to-office budget & margin" },
    ],
    primaryCtaLabel: "Scope field-to-office",
    diagnosticPrompt: "Where is the biggest disconnect between your field sites and head office?",
    diagnosticOptions: [
      {
        id: "whatsapp-chaos",
        label: "Site updates buried in 30 WhatsApp groups",
        hint: "Photos, delays, and material requests get lost in endless chat scroll",
      },
      {
        id: "contractor-billing",
        label: "Unverified contractor & vendor billing",
        hint: "Approving invoices without clear photo proof of completed site milestones",
      },
      {
        id: "offline-capture",
        label: "Field checklists & inspections still on paper",
        hint: "Technicians or engineers re-typing site notes back at the office",
      },
      {
        id: "buyer-portal",
        label: "Property buyers / clients chasing status",
        hint: "Need a branded buyer/tenant portal for handovers, payments & service tickets",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · CHAT GROUP AMNESIA",
        title: "Critical site photos and change orders lost in 400 daily messages",
        body: "Three weeks later, nobody can prove who approved a variation or whether a defect was actually fixed.",
        fix: "Structured site logs pinned to exact floor plans, units, and work orders—in 3 taps.",
        image: "/media/images/solutions/field-service-real-estate.webp",
        imageAlt: "Site engineer logging structured inspection data on mobile",
      },
      {
        symptom: "HEADACHE 02 · BLIND MILESTONE PAYOUTS",
        title: "Finance paying subcontractor bills without verified site proof",
        body: "Head office approves stage payments based on phone calls, discovering incomplete work only at handover.",
        fix: "Geo-tagged, timestamped photo sign-offs required before a milestone invoice unlocks.",
        image: "/media/images/sections/transform.webp",
        imageAlt: "Project managers reviewing verified milestone completions",
      },
      {
        symptom: "HEADACHE 03 · HANDOVER FRICTION",
        title: "Buyers and tenants calling five people for one status update",
        body: "Payment schedules, possession docs, and maintenance requests are scattered across sales and facility teams.",
        fix: "A branded homeowner/tenant portal for stage progress, receipts, and post-handover service.",
        image: "/media/images/deck/deck-hospitality.webp",
        imageAlt: "Warm client handover and property experience",
      },
    ],
    shiftTitle: "Every site, crew, and unit visible on one screen.",
    shiftSubtitle: "Engineered for dusty job sites with spotty signal—and boardrooms that need clean numbers.",
    shiftRows: [
      { before: "30 WhatsApp groups per project", after: "Unit-by-unit progress board with geo-tagged photo evidence" },
      { before: "Paper snag lists re-typed on Friday", after: "Offline mobile capture that auto-assigns fixes to contractors" },
      { before: "Unverified subcontractor claims", after: "Milestone payouts locked to engineer QA sign-off" },
      { before: "Anxious buyers calling for construction updates", after: "Self-serve buyer portal with milestone photos & payment ledger" },
    ],
    modules: [
      {
        tag: "FIELD APP",
        title: "Offline-First Mobile Site & Snag Capture",
        summary: "Capture photos, voice notes, and checklists without signal; auto-syncs the moment connection returns.",
      },
      {
        tag: "CONTRACTOR OPS",
        title: "Work Orders, BOQ & Milestone Billing",
        summary: "Issue work orders, track material consumption, and tie vendor payouts to verified QA sign-off.",
      },
      {
        tag: "CONTROL TOWER",
        title: "Multi-Project Schedule & Margin Board",
        summary: "Spot delayed units, budget overruns, and crew bottlenecks before they hit your timeline.",
      },
      {
        tag: "BUYER / TENANT PORTAL",
        title: "Customer Handover & Facility Desk",
        summary: "Branded portal for payment schedules, document vaults, snag sign-off, and AMC service tickets.",
      },
    ],
    proof: {
      client: "The Livin Hotels & ZPhoenix",
      category: "Spaces, Venues & Operations",
      hook: "Built interactive spatial maps, booking engines, and operational workflows.",
      outcome: "Replaced manual WhatsApp coordination with real-time unit/seat availability and self-serve portals.",
      image: "/media/portfolio/screenshots/livinhotels-home.webp",
      href: "/portfolio/livinhotels",
    },
    faq: [
      {
        q: "What happens when field engineers or technicians are in basements or remote areas with no internet?",
        a: "Yes—offline-first field apps with local sync when signal drops.",
      },
      {
        q: "Will site supervisors and subcontractors actually use it?",
        a: "Yes—because the field UI is designed to be faster than another WhatsApp thread.",
      },
      {
        q: "Can you customize it for both property development and ongoing facility/field maintenance?",
        a: "Yes—development pipelines and ongoing field/facility maintenance in one system.",
      },
    ],
    relatedServiceSlug: "mobile-app-development",
    seoTitle: "Custom Field Service, Construction & Real Estate Software | doimpo",
    seoDescription:
      "Replace chaotic WhatsApp site groups and paper snag lists with offline-ready field apps, contractor milestone portals, and live project margin dashboards.",
  },
  {
    slug: "logistics-supply-chain",
    tier: "vertical",
    shortLabel: "LOGISTICS & FLEET",
    label: "Logistics, Fleet & Last-Mile Delivery Platforms",
    eyebrow: "Logistics, Fleet & Last-Mile",
    headline: "Every load visible.",
    headlineAccent: "Every mile accounted for.",
    subhead:
      "Dispatch, warehouse, and shipper visibility in one live control loop.",
    aeoSummary:
      "doimpo connects dispatch, warehouse, and B2B shippers into one live logistics control loop.",
    heroImage: "/media/images/solutions/logistics-supply-chain.webp",
    heroImageAlt: "Logistics coordinator tracking fleet deliveries and warehouse dispatch",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-logistics-supply-chain.jpg",
    metrics: [
      { value: "Same-Day", label: "ePOD to invoice generation" },
      { value: "Live", label: "GPS & ETA customer tracking" },
      { value: "-60%", label: "“Where is my order?” calls" },
    ],
    primaryCtaLabel: "Scope dispatch & fleet",
    diagnosticPrompt: "Which part of your fleet or delivery operation is costing you the most?",
    diagnosticOptions: [
      {
        id: "manual-dispatch",
        label: "Manual dispatch & driver coordination",
        hint: "Dispatchers spending hours on phone calls and spreadsheets assigning loads",
      },
      {
        id: "paper-pod",
        label: "Delayed paper Proof-of-Delivery (POD) & billing",
        hint: "Waiting days for signed paper slips before finance can invoice the client",
      },
      {
        id: "customer-visibility",
        label: "B2B clients calling for live shipment ETAs",
        hint: "No branded self-serve portal for bookings, live tracking, and customs/shipping docs",
      },
      {
        id: "fleet-costs",
        label: "Fuel, idle time & 3PL reconciliation leaks",
        hint: "Lack of trip-level profitability, vehicle maintenance alerts, or carrier rate checks",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE PAPER POD DELAY",
        title: "Revenue trapped for 14 days waiting on a crumpled delivery slip",
        body: "You delivered the load on Monday, but finance can’t raise the invoice until the driver brings back a signed piece of paper.",
        fix: "Mobile ePOD that triggers the invoice the moment the truck leaves.",
        image: "/media/images/solutions/logistics-supply-chain.webp",
        imageAlt: "Digital proof of delivery and logistics dispatch workflow",
      },
      {
        symptom: "HEADACHE 02 · WHERE IS MY TRUCK?",
        title: "Support teams answering 200 status calls a day",
        body: "Dispatcher calls driver, driver doesn't pick up, customer waits, and everybody loses twenty minutes.",
        fix: "Live map tracking links and automated WhatsApp/SMS milestone alerts for every consignee.",
        image: "/media/images/sections/propel.webp",
        imageAlt: "Real-time logistics control room and customer tracking",
      },
      {
        symptom: "HEADACHE 03 · DISPATCH BOTTLENECKS",
        title: "Routing 50 vehicles from memory and a whiteboard",
        body: "When your senior dispatcher takes a day off, load assignment and capacity planning grind to a halt.",
        fix: "Smart dispatch board with capacity matching, zone grouping, and 1-tap driver manifest push.",
        image: "/media/images/blueprint/execute.webp",
        imageAlt: "Dispatchers coordinating fleet routes on a unified screen",
      },
    ],
    shiftTitle: "From dock to doorstep—without a single phone call.",
    shiftSubtitle: "Connect your dispatch desk, driver cabs, warehouse bays, and B2B shippers in one loop.",
    shiftRows: [
      { before: "Hours of phone calls to assign daily loads", after: "Drag-and-drop dispatch board pushed straight to driver phones" },
      { before: "10–14 day billing lag waiting on paper PODs", after: "Digital signature + photo ePOD triggers instant invoicing" },
      { before: "Customers calling for shipment status", after: "Branded tracking portal with live ETA and downloadable documents" },
      { before: "Blind trip margins and fuel leakage", after: "Per-trip cost, detention time, and carrier margin analytics" },
    ],
    modules: [
      {
        tag: "DISPATCH BOARD",
        title: "Load Planning & Route Assignment",
        summary: "Assign vehicles, optimize multi-drop sequences, and manage own-fleet + 3PL carriers.",
      },
      {
        tag: "DRIVER APP",
        title: "Turn-by-Turn Manifest & Instant ePOD",
        summary: "Simple driver mobile app for stop sequences, barcode scan, digital signatures, and damage photos.",
      },
      {
        tag: "SHIPPER PORTAL",
        title: "B2B Booking, Rate Quote & Live Tracking",
        summary: "Let commercial clients request pickups, view contracted rates, track loads, and download PODs.",
      },
      {
        tag: "BILLING & FLEET",
        title: "Automated Freight Audit & Vehicle Health",
        summary: "Auto-calculate detention/accessorial charges, driver settlements, and service reminders.",
      },
    ],
    proof: {
      client: "Trevia & Enterprise Operations",
      category: "Fleet & Mobility Systems",
      hook: "Engineered high-reliability web platforms and operational tracking interfaces.",
      outcome: "Clear real-time telemetry and streamlined inquiry-to-fulfillment workflows.",
      image: "/media/portfolio/screenshots/trevia-products.webp",
      href: "/portfolio/trevia",
    },
    faq: [
      {
        q: "Can drivers use the app even if they are contractors or 3PL partners?",
        a: "Yes. Contractor and 3PL drivers get scoped access without exposing your full ops stack.",
      },
      {
        q: "Does it integrate with GPS telematics and our ERP/accounting system?",
        a: "Yes—telematics, ERP, and accounting sync into one live dispatch and finance loop.",
      },
      {
        q: "How quickly can we roll this out across our fleet?",
        a: "Core dispatch, ePOD, and tracking usually launch in 6–8 weeks via a pilot route first.",
      },
    ],
    relatedServiceSlug: "mobile-app-development",
    seoTitle: "Custom Logistics, Fleet Management & Last-Mile Software | doimpo",
    seoDescription:
      "Accelerate dispatch and cash flow with custom logistics software: smart route planning, driver ePOD apps, live B2B shipment tracking portals, and automated freight billing.",
  },
  {
    slug: "b2b-commerce-portals",
    tier: "vertical",
    shortLabel: "B2B & DEALER PORTALS",
    label: "Custom B2B Commerce & Dealer/Distributor Portals",
    eyebrow: "B2B Commerce & Dealer Portals",
    headline: "Retire the PDF catalog.",
    headlineAccent: "Let dealers self-serve 24/7.",
    subhead:
      "Dealer portals with account pricing, credit limits, and 60-second bulk reorders.",
    aeoSummary:
      "doimpo builds B2B dealer portals with account pricing, credit limits, and fast bulk reordering.",
    heroImage: "/media/images/solutions/b2b-commerce-portals.webp",
    heroImageAlt: "Wholesale distributor and brand team reviewing a B2B dealer ordering portal",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-b2b-commerce-portals.jpg",
    metrics: [
      { value: "60 sec", label: "100-SKU bulk reorder time" },
      { value: "24/7", label: "Self-serve dealer ordering" },
      { value: "Zero", label: "Pricing or MOQ keying errors" },
    ],
    primaryCtaLabel: "Scope dealer portal",
    diagnosticPrompt: "How are your wholesale buyers, dealers, or distributors ordering today?",
    diagnosticOptions: [
      {
        id: "pdf-whatsapp",
        label: "Orders arriving via PDF, WhatsApp & phone calls",
        hint: "Reps wasting the day retyping dealer orders",
      },
      {
        id: "complex-pricing",
        label: "Complex account pricing, MOQs & credit limits",
        hint: "Shopify/WooCommerce plugins can't handle your real dealer price tiers & terms",
      },
      {
        id: "slow-reorders",
        label: "Slow bulk & matrix re-ordering experience",
        hint: "Buyers refuse 80-click cart workflows",
      },
      {
        id: "claims-statements",
        label: "Dealers chasing invoices, ledgers & warranty claims",
        hint: "No self-serve portal for account statements, dispatch status, or RMA claims",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · REPS AS ORDER CLERKS",
        title: "Your best salespeople spend four hours a day typing WhatsApp orders",
        body: "Dealers send photos of handwritten lists or Excel sheets, and your team manually keys every SKU and checks stock.",
        fix: "A self-serve dealer portal with 1-click repeat orders, CSV upload, and live stock visibility.",
        image: "/media/images/solutions/b2b-commerce-portals.webp",
        imageAlt: "B2B buyer placing a bulk order on a clean digital portal",
      },
      {
        symptom: "HEADACHE 02 · RETAIL CART FRICTION",
        title: "Forcing wholesale buyers through a consumer 'Add to Cart' storefront",
        body: "A distributor ordering 150 variants across sizes and finishes won't click through 150 product pages.",
        fix: "High-speed matrix ordering grid, barcode/SKU quick-entry, and saved order templates.",
        image: "/media/images/deck/deck-commerce.webp",
        imageAlt: "Curated digital commerce experience built by doimpo",
      },
      {
        symptom: "HEADACHE 03 · PRICING & CREDIT CHAOS",
        title: "Wrong discount tier applied—or shipping to an account over its credit limit",
        body: "When contract pricing and net-30 credit rules live in someone's head, margin leaks on every invoice.",
        fix: "Automated customer-specific price books, volume breaks, MOQ enforcement, and live credit-limit gates.",
        image: "/media/images/services/ecommerce.webp",
        imageAlt: "Automated B2B pricing and commerce operations",
      },
    ],
    shiftTitle: "Built for 500-unit wholesale orders—not single T-shirts.",
    shiftSubtitle: "Spreadsheet speed with enterprise commerce guardrails.",
    shiftRows: [
      { before: "Orders sent via WhatsApp, email, and phone calls", after: "24/7 self-serve dealer portal on web and mobile" },
      { before: "Consumer product pages that take 20 minutes to order", after: "60-second matrix grid, CSV upload & 1-click reorder" },
      { before: "Manual price checks & credit limit overrides", after: "Account-specific price lists, MOQs & Net-30/60 credit rules enforced automatically" },
      { before: "Dealers calling finance for ledger statements", after: "Self-serve invoice downloads, credit notes, and live dispatch tracking" },
    ],
    modules: [
      {
        tag: "SPEED ORDERING",
        title: "Quick-Order Matrix & 1-Click Reorder",
        summary: "Spreadsheet-style SKU entry, variant grids, saved seasonal lists, and instant CSV order upload.",
      },
      {
        tag: "PRICE BOOKS",
        title: "Customer-Specific Pricing & MOQ Engine",
        summary: "Contract pricing by dealer tier, volume slabs, case-pack rules, and promotional bundles.",
      },
      {
        tag: "CREDIT & PAY",
        title: "Net Terms, Credit Limits & Multi-Pay",
        summary: "Enforce credit limits, support PO numbers, partial payments, and online ledger settlement.",
      },
      {
        tag: "DEALER HUB",
        title: "Statements, Marketing Assets & Claims",
        summary: "One login for account ledgers, co-branded marketing kits, warranty/RMA claims, and shipment tracking.",
      },
    ],
    proof: {
      client: "Keen Couture & Plant's Mark",
      category: "Commerce & Catalog Systems",
      hook: "Designed and engineered high-conversion digital storefronts and catalog architectures.",
      outcome: "Owned commerce channels that protect brand margin and streamline order operations.",
      image: "/media/portfolio/screenshots/keencouture-home.webp",
      href: "/portfolio/keencouture",
    },
    faq: [
      {
        q: "Why not just use Shopify Plus or WooCommerce with B2B plugins?",
        a: "Shopify Plus is expensive and still weak on dealer credit, rebates, and multi-warehouse ERP rules.",
      },
      {
        q: "Can our field sales reps also use the portal to place orders on behalf of dealers?",
        a: "Yes—reps can open a dealer account on tablet and place the order on the spot.",
      },
      {
        q: "Will it sync with our existing ERP or inventory system?",
        a: "Yes—orders, stock, price tiers, and balances sync bi-directionally with your ERP.",
      },
    ],
    relatedServiceSlug: "ecommerce-solutions",
    seoTitle: "Custom B2B E-Commerce, Wholesale & Dealer Portal Development | doimpo",
    seoDescription:
      "Replace PDF catalogs and manual wholesale order entry with a custom B2B dealer & distributor portal—account-specific pricing, credit limits, and 60-second bulk reorders.",
  },
  {
    slug: "b2b-corporate-rebranding",
    tier: "brand",
    shortLabel: "CORPORATE REBRAND",
    label: "B2B Corporate Rebranding & Modernization",
    eyebrow: "Corporate Rebranding & Modernization",
    headline: "Turn outdated legacy perception into",
    headlineAccent: "market authority.",
    subhead:
      "Strategy, identity, and sales decks that match the scale of deals you already close.",
    aeoSummary:
      "doimpo rebuilds legacy B2B identities with strategy, visuals, and sales enablement so enterprise buyers take you seriously.",
    heroImage: "/media/images/solutions/editorial-rebranding.webp",
    heroImageAlt: "Luxury corporate rebranding monograph and identity system with Space Grotesk typography",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-b2b-corporate-rebranding.jpg",
    metrics: [
      { value: "3.2x", label: "Avg. deal size post-rebrand" },
      { value: "6–8 wks", label: "Executive sprint to rollout" },
      { value: "Zero", label: "Disruption to live revenue" },
    ],
    primaryCtaLabel: "Request brand teardown",
    diagnosticPrompt: "What is the primary trigger forcing your corporate rebrand?",
    diagnosticOptions: [
      {
        id: "lost-deals",
        label: "Losing deals to prettier competitors",
        hint: "Prospects perceive your firm as legacy or behind the times",
      },
      {
        id: "outgrown-startup",
        label: "Outgrown our early startup look",
        hint: "Identity and messaging don't reflect current enterprise scale",
      },
      {
        id: "ma-pivot",
        label: "Post-M&A consolidation or market pivot",
        hint: "Need to unify multiple acquisitions under one coherent master brand",
      },
      {
        id: "funding-exit",
        label: "Preparing for next funding round or exit",
        hint: "Need to maximize company valuation with institutional market authority",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE LEGACY PENALTY",
        title: "Losing 6-figure enterprise deals to slicker, less-capable rivals",
        body: "Enterprise buying committees judge credibility within seconds.",
        fix: "An institutional visual identity and high-trust design system built for enterprise procurement confidence.",
        image: "/media/images/solutions/editorial-headache-legacy.webp",
        imageAlt: "Executive procurement dossier comparing legacy proposal against sleek modernized brand system",
      },
      {
        symptom: "HEADACHE 02 · MARGIN EROSION",
        title: "Drowning in commoditization and price wars",
        body: "When your brand looks identical to every standard vendor, buyers treat you like a line-item commodity and demand discounts.",
        fix: "Category-defining narrative and high-authority aesthetic positioning that commands premium contract values.",
        image: "/media/images/solutions/editorial-headache-pricing.webp",
        imageAlt: "Editorial diagram comparing commodity discount erosion against brand premium margin curve",
      },
      {
        symptom: "HEADACHE 03 · MESSAGING DEBT",
        title: "Sales reps improvising their own rogue pitch decks",
        body: "Your team has launched new offerings and evolved your core value, but marketing materials lag behind.",
        fix: "Unified sales enablement collateral, pitch deck architectures, and one-pagers that align the entire organization.",
        image: "/media/images/solutions/editorial-headache-decks.webp",
        imageAlt: "Rogue slide clutter replaced by unified brand identity guidelines monograph",
      },
    ],
    shiftTitle: "From apologizing for your brand to leading your category.",
    shiftSubtitle: "What changes when your corporate identity matches your true operational scale.",
    shiftRows: [
      { before: "Apologizing for an outdated website on sales calls", after: "High-authority digital presence that pre-sells enterprise trust" },
      { before: "Prospects comparing you on price against budget vendors", after: "Recognized as the premium category benchmark with margin power" },
      { before: "Sales reps duct-taping custom slides in PowerPoint", after: "Battle-tested, modular sales decks and battlecards in Figma" },
      { before: "Confusing sub-brands and conflicting regional collateral", after: "Streamlined brand architecture and living digital style guides" },
    ],
    modules: [
      {
        tag: "EXECUTIVE AUDIT",
        title: "Brand Equity & Perception Diagnostics",
        summary: "In-depth competitor whitespace audit, stakeholder interviews, and enterprise customer perception teardown.",
      },
      {
        tag: "STRATEGY & NARRATIVE",
        title: "Category Positioning & Value Proposition Matrix",
        summary: "A crisp 1-sentence value claim, buyer-persona pain mapping, and high-impact brand manifesto.",
      },
      {
        tag: "VISUAL SYSTEM",
        title: "Corporate Identity & Typography Suite",
        summary: "Modernized logo marks, responsive wordmarks, bespoke color architecture, and typography systems.",
      },
      {
        tag: "SALES ENABLEMENT",
        title: "Executive Pitch Decks & Collateral Suite",
        summary: "12-slide enterprise pitch deck, case study templates, capability sheets, and proposal themes.",
      },
      {
        tag: "DIGITAL ROLLOUT",
        title: "Brand Guidelines & Asset Hub",
        summary: "Cloud-hosted brand portal with downloadable vector assets, guidelines, and design tokens.",
      },
      {
        tag: "MIGRATION GOVERNANCE",
        title: "Risk-Free Brand Launch Roadmap",
        summary: "Internal town-hall launch playbook, client announcement templates, and SEO-safe migration protocol.",
      },
    ],
    proof: {
      client: "Enterprise Tech & Growth Scaleups",
      category: "B2B Corporate Rebranding",
      hook: "From fragmented legacy identity to unified market authority",
      outcome: "+180% inbound enterprise pipeline value and 35% shorter procurement cycles within 90 days of brand launch.",
      image: "/media/images/solutions/b2b-corporate-rebranding.webp",
      href: "/book?from=solution&title=B2B+Corporate+Rebranding&service=b2b-corporate-rebranding",
    },
    faq: [
      {
        q: "How long does a full corporate rebrand take with doimpo?",
        a: "Full corporate rebrand sprint: 6–8 weeks from audit to rollout.",
      },
      {
        q: "Will a corporate rebrand hurt our existing SEO or customer brand recognition?",
        a: "No. We preserve equity with redirects, retained domains, and coordinated customer rollout.",
      },
      {
        q: "What deliverables are included at handoff?",
        a: "Logo suites, type/color rules, Figma source, pitch deck templates, and a living brand portal.",
      },
      {
        q: "Can you also redesign and build our new marketing website?",
        a: "Yes—brand identity and Next.js web build under one roof, no agency handoff tax.",
      },
    ],
    relatedServiceSlug: "branding-design",
    seoTitle: "B2B Corporate Rebranding Agency & Modernization Services | doimpo",
    seoDescription:
      "Rebuild outdated B2B branding into an enterprise-grade market leader. Strategy, visual identity systems, and sales enablement that command 7-figure deals.",
  },
  {
    slug: "brand-strategy-positioning",
    tier: "brand",
    shortLabel: "BRAND STRATEGY",
    label: "Brand Strategy, Category Design & Positioning",
    eyebrow: "Brand Strategy & Category Design",
    headline: "Stop competing in price wars.",
    headlineAccent: "Own your own category.",
    subhead:
      "Category positioning that ends price wars and pre-sells the deal.",
    aeoSummary:
      "doimpo defines uncontested category positioning and messaging that stops price wars and shortens sales cycles.",
    heroImage: "/media/images/solutions/editorial-brand-strategy.webp",
    heroImageAlt: "Editorial category design matrices and positioning monograph on architectural slate",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-brand-strategy-positioning.jpg",
    metrics: [
      { value: "100%", label: "Uncontested category clarity" },
      { value: "45%", label: "Shorter sales consideration" },
      { value: "4 wks", label: "Strategic positioning sprint" },
    ],
    primaryCtaLabel: "Book positioning audit",
    diagnosticPrompt: "Where is your current market positioning failing most?",
    diagnosticOptions: [
      {
        id: "me-too",
        label: "Prospects confuse us with competitors",
        hint: "We sound just like everyone else in demos and on our website",
      },
      {
        id: "pricing-pressure",
        label: "Unable to justify premium pricing",
        hint: "Deals stall on price because buyers see us as a commodity",
      },
      {
        id: "multiple-personas",
        label: "Fragmented multi-audience messaging",
        hint: "Trying to talk to everyone and failing to convert anyone",
      },
      {
        id: "sales-friction",
        label: "Long sales cycles & indecisive buyers",
        hint: "Prospects understand features but don't see urgent business impact",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE 'ME-TOO' JARGON TRAP",
        title: "Drowning in generic buzzwords that leave zero impression",
        body: "Claiming you are 'AI-powered, scalable, and customer-obsessed' makes you sound like 500 other competitors.",
        fix: "A category POV that names who it’s for—and why any other way costs more.",
        image: "/media/images/solutions/editorial-brand-strategy.webp",
        imageAlt: "Clear strategic positioning framework replacing buzzwords",
      },
      {
        symptom: "HEADACHE 02 · FEATURE-FIRST POSITIONING",
        title: "Selling technical features instead of high-value business outcomes",
        body: "Teams sell engine specs while budget holders need business outcomes.",
        fix: "A tiered messaging architecture that maps technical features directly to C-level revenue, risk, and efficiency outcomes.",
        image: "/media/images/solutions/editorial-headache-pricing.webp",
        imageAlt: "Editorial margin and positioning curve eliminating feature fatigue",
      },
      {
        symptom: "HEADACHE 03 · THE COMMODITY TRAP",
        title: "Always being compared to the low-cost overseas alternative",
        body: "Without crisp positioning, you are evaluated inside someone else's category definition—often one where cheap, low-touch providers set the price anchor.",
        fix: "Category design that puts you in a class of one.",
        image: "/media/images/solutions/editorial-headache-legacy.webp",
        imageAlt: "Strategic board defining brand differentiation and category moats",
      },
    ],
    shiftTitle: "From blending into the noise to defining the standard.",
    shiftSubtitle: "How positioning shifts customer perception from 'just another vendor' to 'indispensable partner.'",
    shiftRows: [
      { before: "“We are an all-in-one software platform for teams”", after: "Crisp, category-defining claim targeting one urgent pain point" },
      { before: "Prospects debating line-item costs against competitors", after: "Prospects asking how quickly you can start implementation" },
      { before: "Marketing and sales telling two different stories", after: "Single unified messaging matrix used across web, ads, and pitches" },
      { before: "Long, meandering discovery calls trying to explain value", after: "Prospects arrive already sold on your philosophy and method" },
    ],
    modules: [
      {
        tag: "WHITESPACE MAPPING",
        title: "Market & Competitor Landscape Teardown",
        summary: "Exposing competitor messaging gaps, category clichés, and unexploited customer frustrations.",
      },
      {
        tag: "BUYER PSYCHOLOGY",
        title: "ICP Pain & Motivation Profiling",
        summary: "Uncovering the real emotional and commercial triggers that force budget approval from economic buyers.",
      },
      {
        tag: "VALUE ARCHITECTURE",
        title: "Core Value Proposition Matrix",
        summary: "Primary headline, 10-second elevator pitch, objection-handling pillars, and proof-point hierarchy.",
      },
      {
        tag: "CATEGORY DESIGN",
        title: "Category Point-of-View & Manifesto",
        summary: "Defining the 'Old Way vs. New Way' narrative that makes staying with the status quo feel unacceptably risky.",
      },
      {
        tag: "SALES SCRIPTING",
        title: "Verbal Identity & Objection Playbook",
        summary: "Scripts for SDRs, AEs, and founders to shut down competitor comparisons on the call.",
      },
      {
        tag: "EXECUTIVE ALIGNMENT",
        title: "Leadership Strategy Playbook",
        summary: "One voice for founders, product, marketing, and sales—shared talk-tracks and priorities.",
      },
    ],
    proof: {
      client: "B2B SaaS & Enterprise Consultancies",
      category: "Brand Strategy & Positioning",
      hook: "Carving uncontested market category whitespace",
      outcome: "+40% higher demo-to-close conversion and eliminated price resistance on enterprise contracts.",
      image: "/media/images/solutions/brand-strategy-positioning.webp",
      href: "/book?from=solution&title=Brand+Strategy+%26+Positioning&service=brand-strategy-positioning",
    },
    faq: [
      {
        q: "What is the difference between brand positioning and visual identity?",
        a: "Visual identity is how you look. Positioning is why buyers pay you a premium.",
      },
      {
        q: "How do we know if our company has a positioning problem?",
        a: "If every deal stalls on price, or it takes 45 minutes just to explain what you do—you have a positioning problem.",
      },
      {
        q: "Who from our team needs to be involved in the strategy sprint?",
        a: "Usually CEO, marketing, and sales leads—two 90-minute workshops. We do the synthesis.",
      },
      {
        q: "How does brand strategy integrate into our website and sales materials?",
        a: "We deliver hero structures, talk-tracks, and messaging cards ready to deploy immediately.",
      },
    ],
    relatedServiceSlug: "strategy-consulting",
    seoTitle: "Brand Strategy & B2B Category Positioning Agency | doimpo",
    seoDescription:
      "Stop competing on price. We define your distinct market whitespace, craft undeniable value propositions, and engineer category-defining brand strategy.",
  },
  {
    slug: "enterprise-design-systems",
    tier: "brand",
    shortLabel: "DESIGN SYSTEMS",
    label: "Enterprise Design Systems & Brand Governance",
    eyebrow: "Design Systems & Brand Governance",
    headline: "Scale brand consistency across teams",
    headlineAccent: "without the design bottleneck.",
    subhead:
      "Figma tokens synced to React components—one library for design and code.",
    aeoSummary:
      "doimpo builds Figma design systems synced to production React components so teams ship without brand drift.",
    heroImage: "/media/images/solutions/editorial-design-systems.webp",
    heroImageAlt: "Architectural dark-mode UI design system specimen spread showing typography and design tokens",
    ogImage: "https://doimpo.com/media/brand/og-doimpo-solution-enterprise-design-systems.jpg",
    metrics: [
      { value: "4x", label: "Faster design-to-code velocity" },
      { value: "100%", label: "Figma-to-code token sync" },
      { value: "Zero", label: "Brand fragmentation across teams" },
    ],
    primaryCtaLabel: "Audit design system",
    diagnosticPrompt: "What is your biggest design system obstacle right now?",
    diagnosticOptions: [
      {
        id: "figma-code-gap",
        label: "Figma-to-code disconnect",
        hint: "Designs in Figma don't match what engineers build in production",
      },
      {
        id: "inconsistent-brand",
        label: "Inconsistent UI across products & regions",
        hint: "Every team, app, or regional website looks like a separate company",
      },
      {
        id: "slow-velocity",
        label: "Slow time-to-market on new features",
        hint: "Designers and engineers waste weeks reinventing basic components",
      },
      {
        id: "dead-guidelines",
        label: "Static PDF guidelines nobody follows",
        hint: "80-page brand guide sits in Drive while teams build rogue pages",
      },
    ],
    headaches: [
      {
        symptom: "HEADACHE 01 · THE DESIGN-ENGINEERING CHASM",
        title: "What is approved in Figma never matches production code",
        body: "Designers create pixel-perfect mockups, but developers eyeball spacing, hex codes, and border radii.",
        fix: "Design tokens that sync from Figma into Tailwind, CSS variables, and React.",
        image: "/media/images/solutions/editorial-design-systems.webp",
        imageAlt: "Design token mapping interface connecting Figma and React",
      },
      {
        symptom: "HEADACHE 02 · CRIPPLING REWORK",
        title: "Reinventing the wheel every time a new page or feature is needed",
        body: "Without a modular component library, developers and designers build buttons, tables, navigation bars, and modals from scratch on every sprint.",
        fix: "A production-tested library of accessible, keyboard-navigable components ready for instant assembly.",
        image: "/media/images/solutions/editorial-headache-decks.webp",
        imageAlt: "Modular UI component kit replacing fragmented duplicate code",
      },
      {
        symptom: "HEADACHE 03 · BRAND FRAGMENTATION AT SCALE",
        title: "Every department and contractor inventing their own visual rules",
        body: "As headcount grows, marketing, product, and sales operate in silos.",
        fix: "A living, searchable digital documentation hub with strict governance and role-based permissions.",
        image: "/media/images/solutions/editorial-headache-legacy.webp",
        imageAlt: "Unified design governance documentation hub",
      },
    ],
    shiftTitle: "From fragmented guesswork to synchronized velocity.",
    shiftSubtitle: "What changes when Figma tokens and React components stay in sync.",
    shiftRows: [
      { before: "Designers re-drawing button variants for every new screen", after: "Drag-and-drop auto-layout components in Figma in seconds" },
      { before: "Developers building UI elements from scratch with custom CSS", after: "Importing battle-tested, accessible React/Tailwind components" },
      { before: "Brand updates requiring manual code changes across 20 repos", after: "Single design token update instantly cascades across all apps" },
      { before: "Static 70-page brand guidelines PDF sitting unread", after: "Interactive living documentation with live code previews" },
    ],
    modules: [
      {
        tag: "TOKEN ARCHITECTURE",
        title: "Three-Tier Design Token Engine",
        summary: "Primitive, semantic, and component tokens covering colors, spacing, elevation, radius, and typography.",
      },
      {
        tag: "FIGMA MASTER LIBRARY",
        title: "Production Figma Component Library",
        summary: "Auto-layout 5.0, variables, component properties, interactive variants, and accessible contrast ratios.",
      },
      {
        tag: "CODE SYNCHRONIZATION",
        title: "Production React & Tailwind Component Suite",
        summary: "Pixel-perfect TypeScript components built on accessible primitives (Radix UI / Tailwind CSS).",
      },
      {
        tag: "THEMING & MODES",
        title: "Multi-Brand & Dark/Light Mode Architecture",
        summary: "White-label theming and light/dark modes from one token set—no flicker, no fork.",
      },
      {
        tag: "DOCUMENTATION HUB",
        title: "Interactive Storybook & Governance Portal",
        summary: "Searchable docs with live props, do’s/don’ts, and accessibility rules.",
      },
      {
        tag: "TEAM ONBOARDING",
        title: "Design System Adoption Workshops",
        summary: "Hands-on training so designers and engineers ship from the same library.",
      },
    ],
    proof: {
      client: "Multi-Product SaaS & Scaleup Platforms",
      category: "Enterprise Design Systems",
      hook: "From UI fragmentation to 4x engineering velocity",
      outcome: "Reduced front-end sprint delivery time by 62% and eliminated visual design bugs across 3 global software products.",
      image: "/media/images/solutions/enterprise-design-systems.webp",
      href: "/book?from=solution&title=Enterprise+Design+Systems&service=enterprise-design-systems",
    },
    faq: [
      {
        q: "Is this just a Figma UI kit, or does it include actual code?",
        a: "Both—Figma library, token pipeline, and production React components that match 1:1.",
      },
      {
        q: "What design frameworks or libraries do you build upon?",
        a: "Tailwind, Radix, and headless patterns—WCAG AA, lean bundles, zero proprietary lock-in.",
      },
      {
        q: "Can our existing developers maintain the design system after handoff?",
        a: "Yes. Versioning, Storybook, CI workflows, and hands-on training are part of handoff.",
      },
      {
        q: "How does the design system support multiple brands or white-label products?",
        a: "Semantic tokens let you re-theme for white-label or subsidiaries by swapping a theme file.",
      },
    ],
    relatedServiceSlug: "web-development",
    seoTitle: "Enterprise Design Systems & Brand Governance Agency | doimpo",
    seoDescription:
      "Scale brand consistency across product and marketing teams. Production Figma design systems, design tokens, and synchronized React component libraries.",
  },
];

export const CAPABILITY_SOLUTIONS = SOLUTIONS.filter((s) => s.tier === "capability");
export const VERTICAL_SOLUTIONS = SOLUTIONS.filter((s) => s.tier === "vertical");
export const BRAND_SOLUTIONS = SOLUTIONS.filter((s) => s.tier === "brand");

export function getSolutionBySlug(slug?: string): SolutionCategory | undefined {
  if (!slug) return undefined;
  return SOLUTIONS.find((s) => s.slug === slug);
}

function esc(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Semantic HTML for static prerendering and AI/search crawlers on /solutions. */
export function solutionsHubSeoHtml(): string {
  const items = SOLUTIONS.map(
    (s) =>
      `<li><a href="/solutions/${esc(s.slug)}"><strong>${esc(s.label)}</strong></a> — ${esc(s.headline)} ${esc(s.headlineAccent)} ${esc(s.aeoSummary)}</li>`,
  ).join("");
  return `<article>
    <h1>Custom Software Solutions You Own—No Per-Seat Tax</h1>
    <p>${esc(SOLUTIONS_HUB_META.description)}</p>
    <h2>10 Custom Software Solution Categories</h2>
    <ul>${items}</ul>
    <p><a href="/book?utm_source=solutions-hub">Book a 30-minute technical scoping call</a> · <a href="mailto:hello@doimpo.com">hello@doimpo.com</a></p>
  </article>`;
}

/** Semantic HTML for static prerendering and AI/search crawlers on /solutions/[slug]. */
export function solutionSeoHtml(solution: SolutionCategory): string {
  const headaches = solution.headaches
    .map(
      (h) =>
        `<li><strong>${esc(h.title)}</strong>: ${esc(h.body)} <em>Fix: ${esc(h.fix)}</em></li>`,
    )
    .join("");
  const shifts = solution.shiftRows
    .map((r) => `<li><strong>Before:</strong> ${esc(r.before)} → <strong>With doimpo:</strong> ${esc(r.after)}</li>`)
    .join("");
  const modules = solution.modules
    .map((m) => `<li><strong>${esc(m.title)} (${esc(m.tag)})</strong>: ${esc(m.summary)}</li>`)
    .join("");
  const faqs = solution.faq
    .map((f) => `<section><h3>${esc(f.q)}</h3><p>${esc(f.a)}</p></section>`)
    .join("");

  return `<article>
    <h1>${esc(solution.label)} — ${esc(solution.headline)} ${esc(solution.headlineAccent)}</h1>
    <p><strong>Direct Answer:</strong> ${esc(solution.aeoSummary)}</p>
    <p>${esc(solution.subhead)}</p>
    <h2>The 3 Core Problems We Fix</h2>
    <ul>${headaches}</ul>
    <h2>${esc(solution.shiftTitle)}</h2>
    <ul>${shifts}</ul>
    <h2>Core Modules & Capabilities</h2>
    <ul>${modules}</ul>
    <h2>Client Proof: ${esc(solution.proof.client)}</h2>
    <p>${esc(solution.proof.hook)} — ${esc(solution.proof.outcome)} (<a href="${esc(solution.proof.href)}">View case study</a>)</p>
    <h2>Frequently Asked Questions</h2>
    ${faqs}
    <p>Serving businesses across the United States, United Kingdom, Australia, United Arab Emirates, and India. <a href="/book?utm_source=solution-${esc(solution.slug)}">${esc(solution.primaryCtaLabel)}</a></p>
  </article>`;
}

/** Rich JSON-LD schema for /solutions hub (CollectionPage + ItemList + Service). */
export function solutionsHubJsonLd(): Record<string, unknown>[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: SOLUTIONS_HUB_META.title,
      description: SOLUTIONS_HUB_META.description,
      url: SOLUTIONS_HUB_META.url,
      image: SOLUTIONS_HUB_META.ogImage,
      isPartOf: { "@type": "WebSite", name: "doimpo", url: "https://doimpo.com" },
      provider: {
        "@type": "Organization",
        name: "doimpo",
        url: "https://doimpo.com",
        email: "hello@doimpo.com",
        areaServed: ["US", "GB", "AU", "AE", "IN"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "doimpo Custom Software Solutions",
      itemListElement: SOLUTIONS.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.label,
          description: s.aeoSummary,
          url: `https://doimpo.com/solutions/${s.slug}`,
          provider: { "@type": "Organization", name: "doimpo", url: "https://doimpo.com" },
        },
      })),
    },
  ];
}

/** Rich JSON-LD schema for /solutions/[slug] (Service + FAQPage + BreadcrumbList). */
export function solutionJsonLd(solution: SolutionCategory): Record<string, unknown>[] {
  const url = `https://doimpo.com/solutions/${solution.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: solution.label,
      serviceType: solution.eyebrow,
      description: solution.aeoSummary,
      url,
      image: solution.ogImage,
      provider: {
        "@type": "Organization",
        name: "doimpo",
        url: "https://doimpo.com",
        email: "hello@doimpo.com",
      },
      areaServed: [
        { "@type": "Country", name: "United States" },
        { "@type": "Country", name: "United Kingdom" },
        { "@type": "Country", name: "Australia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "India" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `${solution.label} Modules`,
        itemListElement: solution.modules.map((m) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: m.title,
            description: m.summary,
          },
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: solution.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://doimpo.com" },
        { "@type": "ListItem", position: 2, name: "Solutions", item: "https://doimpo.com/solutions" },
        { "@type": "ListItem", position: 3, name: solution.label, item: url },
      ],
    },
  ];
}
